import React, { useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  FormHelperText,
  Grid,
  IconButton,
  InputAdornment,
  Stack,
  Typography,
} from '@mui/material';
import CustomTextInput from '../../../components/CustomTextInput';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { authApiClient } from '../../../utils/axios';
import { showSnackbar } from '../../../api/snackbarSlice';
import logo from '../../../assets/logo/marmin_logo.png';
import { get } from 'lodash';
import { Visibility, VisibilityOff } from '@mui/icons-material';

const SignupValidationSchema = Yup.object().shape({
  email: Yup.string().email('Invalid email format').required('Email is required'),
  name: Yup.string()
    .required('Name is required')
    .min(3, 'Name must be at least 3 characters')
    .max(25, 'Name must be at most 25 characters'),
  password: Yup.string()
    .min(8, 'Password must be at least 8 characters')
    .matches(/[A-Z]/, 'Password must contain at least one uppercase letter')
    .matches(/[a-z]/, 'Password must contain at least one lowercase letter')
    .matches(/\d/, 'Password must contain at least one number')
    .matches(/[@$!%*?&]/, 'Password must contain at least one special character (@, $, !, %, *, ?, &)')
    .required('Password is required'),
  confirmPassword: Yup.string().oneOf([Yup.ref('password'), null], 'Passwords must match'),
  termsAndCondition: Yup.boolean().oneOf([true], 'You must accept the terms and conditions'),
});

const SignupPasswordForm = ({ email }) => {
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    trigger,
  } = useForm({
    resolver: yupResolver(SignupValidationSchema),
    defaultValues: {
      email: email,
      termsAndCondition: false,
    },
  });

  const onSubmit = async (data) => {
    setLoading(true);
    authApiClient
      .post('/register', data)
      .then((res) => {
        navigate('/signup', { replace: true, state: { email: data.email, stage: 'verification' } });
        dispatch(
          showSnackbar({
            message: 'Signup successful! Please check your email for verification.',
            severity: 'success',
          }),
        );
      })
      .catch((err) => {
        let message =
          Object.values(err?.response?.data?.errors ?? [])
            .flat()
            .join('\n') ||
          err.message ||
          'Something went wrong';
        dispatch(showSnackbar({ message, severity: 'error' }));
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const formData = watch();

  return (
    <>
      <Stack sx={{ alignItems: 'center', mb: 4 }}>
        <img src={logo} alt="logo" width="50%" />
      </Stack>

      <Stack spacing={2}>
        <CustomTextInput name="email" label="Email" control={control} errors={errors} disabled={true} />
        <CustomTextInput name="name" label="Name" control={control} errors={errors} />
        <CustomTextInput
          name="password"
          type={showPassword ? 'text' : 'password'}
          label="Password"
          control={control}
          errors={errors}
          endAdornment={
            <InputAdornment position="end">
              <IconButton onClick={() => setShowPassword((prev) => !prev)} edge="end">
                {showPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          }
        />
        <CustomTextInput
          name="confirmPassword"
          type={showConfirmPassword ? 'text' : 'password'}
          label="Confirm Password"
          control={control}
          errors={errors}
          endAdornment={
            <InputAdornment position="end">
              <IconButton onClick={() => setShowConfirmPassword((prev) => !prev)} edge="end">
                {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          }
        />
        <Grid container spacing={0}>
          <Grid size={6}>
            <Typography variant="caption" sx={{ color: formData.password?.length >= 8 ? 'green' : '#ff0000' }}>
              Minimum 8 characters.
            </Typography>
          </Grid>
          <Grid size={6}>
            <Typography variant="caption" sx={{ color: /[A-Z]/.test(formData.password) ? 'green' : '#ff0000' }}>
              At least 1 uppercase letter.
            </Typography>
          </Grid>

          <Grid size={6}>
            <Typography variant="caption" sx={{ color: /[0-9]/.test(formData.password) ? 'green' : '#ff0000' }}>
              At least 1 number.
            </Typography>
          </Grid>
          <Grid size={6}>
            <Typography
              variant="caption"
              sx={{ color: /[!@#$%^&*(),.?":{}|<>]/.test(formData.password) ? 'green' : '#ff0000' }}
            >
              At least one special character.
            </Typography>
          </Grid>
        </Grid>
        <Controller
          name="termsAndCondition"
          control={control}
          defaultValue={false}
          render={({ field }) => (
            <FormControlLabel
              control={
                <Checkbox
                  {...field}
                  checked={field.value}
                  color="primary"
                  onChange={(e) => {
                    field.onChange(e.target.checked);
                  }}
                />
              }
              label={
                <Box>
                  I agree to the{' '}
                  <a
                    href="https://marmin.ai/terms-of-use/"
                    target="_blank"
                    rel="noreferrer"
                    style={{ textDecoration: 'none' }}
                  >
                    terms & conditions
                  </a>{' '}
                  and{' '}
                  <a
                    href="https://marmin.ai/privacy-policy/"
                    target="_blank"
                    rel="noreferrer"
                    style={{ textDecoration: 'none' }}
                  >
                    privacy policy
                  </a>{' '}
                  of Marmin platform.
                </Box>
              }
            />
          )}
        />
        {errors.termsAndCondition && (
          <FormHelperText error>{get(errors, 'termsAndCondition.message', '')}</FormHelperText>
        )}
        <Button type="button" variant="contained" className="submit" onClick={handleSubmit(onSubmit)}>
          {loading ? 'Signing up...' : 'Sign Up'}
        </Button>
        <Typography variant="body2">
          <Link to="/login" style={{ textDecoration: 'none' }}>
            Already have an account? Sign In
          </Link>
        </Typography>
      </Stack>
    </>
  );
};

export default SignupPasswordForm;
